import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  type ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { EllipsisDirective } from '../../directives/ellipsis/ellipsis';
import { resolveWikipediaLinks } from '../../helpers/wikipedia';
import { resolveWiktionaryLinks } from '../../helpers/wiktionary';
import { TextInfoService } from '../../services/text-info/text-info';
import { TextInfoContentComponent } from './text-info-content/text-info-content';
import { TextInfoSkeletonComponent } from './text-info-skeleton/text-info-skeleton';

@Component({
  selector: 'nr-text-info',
  imports: [TranslatePipe, TextInfoContentComponent, EllipsisDirective, TextInfoSkeletonComponent],
  templateUrl: './text-info.html',
  styleUrl: './text-info.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TextInfoComponent {
  /** Matches $slide-duration in text-info.scss so the height change runs alongside the header fade. */
  private static readonly RESIZE_DURATION_MS = 250;
  private static readonly HEIGHT_CHANGE_THRESHOLD = 1;

  private readonly textInfoService = inject(TextInfoService);
  private readonly body = viewChild.required<ElementRef<HTMLElement>>('body');
  private readonly bodyContent = viewChild.required<ElementRef<HTMLElement>>('bodyContent');
  readonly showMoreInfo = signal<'dictionary' | 'wikipedia' | undefined>(undefined);
  readonly textInfoResource = this.textInfoService.textInfoResource;
  readonly translationFallbackResource = this.textInfoService.translationFallbackResource;
  readonly selectedText = this.textInfoService.selectedText;
  readonly textInfoResponse = computed(() =>
    this.textInfoResource.hasValue() ? this.textInfoResource.value() : undefined,
  );
  readonly translation = computed(
    () =>
      this.textInfoResponse()?.translation ??
      (this.translationFallbackResource.hasValue()
        ? this.translationFallbackResource.value()
        : undefined),
  );
  readonly isLoading = computed(
    () =>
      this.textInfoResource.isLoading() ||
      (this.textInfoResource.status() === 'error' && this.translationFallbackResource.isLoading()),
  );
  readonly isWiktionaryResponse = computed<boolean>(() => {
    const sections = this.textInfoResponse()?.textInfo.wiktionaryResponse?.sections;
    return Boolean(sections && sections.length > 0);
  });
  readonly hasDictionaryInfo = computed(
    () => this.isWiktionaryResponse() || Boolean(this.textInfoResponse()?.textInfo.definition),
  );
  readonly hasWikipediaInfo = computed(() => Boolean(this.textInfoResponse()?.textInfo.info));
  readonly isResolved = computed(
    () => this.hasDictionaryInfo() || this.hasWikipediaInfo() || Boolean(this.translation()),
  );
  readonly isOpen = computed(() => this.isLoading() || this.isResolved());
  readonly wiktionaryHtml = computed(() =>
    resolveWiktionaryLinks(this.textInfoResponse()?.textInfo.wiktionaryResponse?.sections[0]?.text),
  );
  readonly wikipediaHtml = computed(() =>
    resolveWikipediaLinks(
      this.textInfoResponse()?.textInfo.info,
      this.translation()?.detectedSourceLanguage,
    ),
  );

  readonly moreInfoTitle = computed<string>(() => {
    if (this.showMoreInfo() === 'wikipedia') return 'WORD_INFO.WIKIPEDIA';
    return this.isWiktionaryResponse() ? 'WORD_INFO.DICTIONARY' : 'WORD_INFO.MORE_INFO';
  });

  constructor() {
    afterRenderEffect((onCleanup) => {
      onCleanup(
        this.animateHeightChanges(this.body().nativeElement, this.bodyContent().nativeElement),
      );
    });
  }

  closeMoreInfo(): void {
    this.showMoreInfo.set(undefined);
  }

  handleCloseDialog(): void {
    this.textInfoService.clearTextInfo();
    this.showMoreInfo.set(undefined);
  }

  /**
   * Animates the dialog body between its old and new height whenever what it shows resizes:
   * skeleton → result, opening or leaving a detail, translations arriving late… The ResizeObserver
   * callback runs after layout but before paint, so the animation starts on the very frame the size
   * changes, without a jump.
   */
  private animateHeightChanges(body: HTMLElement, bodyContent: HTMLElement): () => void {
    let lastHeight = body.getBoundingClientRect().height;
    let animation: Animation | undefined = undefined;

    const observer = new ResizeObserver(() => {
      // Mid-animation the box shows an interpolated height: start from there so it doesn't jump.
      const fromHeight =
        animation?.playState === 'running' ? body.getBoundingClientRect().height : lastHeight;
      animation?.cancel();
      const toHeight = body.getBoundingClientRect().height;
      lastHeight = toHeight;

      // A closed (hidden) dialog measures 0, and closing swaps the result back to the skeleton:
      // neither should animate.
      if (
        !this.isOpen() ||
        fromHeight === 0 ||
        toHeight === 0 ||
        Math.abs(toHeight - fromHeight) <= TextInfoComponent.HEIGHT_CHANGE_THRESHOLD ||
        matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        return;
      }
      // overflow hidden clips the content while the box is shorter than it.
      animation = body.animate(
        [
          { height: `${fromHeight}px`, overflow: 'hidden' },
          { height: `${toHeight}px`, overflow: 'hidden' },
        ],
        { duration: TextInfoComponent.RESIZE_DURATION_MS, easing: 'ease-out' },
      );
    });
    observer.observe(bodyContent);

    return (): void => {
      observer.disconnect();
      animation?.cancel();
    };
  }
}
